import React from "react";
import { Link } from "react-router-dom";
import CcgRoutePremiumPage from "../components/CcgRoutePremiumPage";
import PostcodeQuotenew from "./PostcodeQuotenew";
import "./CcgRoutePremium.css";

const cardGrid = (className, cards) => (
  <div className={className}>
    {cards.map((card) => (
      <article className={card.className || ""} key={card.title}>
        <h3>{card.title}</h3>
        {card.body ? <p>{card.body}</p> : null}
        {card.items ? <ul>{card.items.map((item) => <li key={item}>{item}</li>)}</ul> : null}
        {card.link ? <Link to={card.link.to} className={card.link.className || "route-link-inline"}>{card.link.label}</Link> : null}
        {card.links ? <div className="route-link-btn-row">{card.links.map((link) => <Link to={link.to} className={link.className || "route-link-btn"} key={link.to}>{link.label}</Link>)}</div> : null}
      </article>
    ))}
  </div>
);

const ascotToHeathrowRoute = {
  id: "ascot-to-heathrow",
  path: "/ascot-to-heathrow",
  liveStripVariant: "windsor",
  seo: {
    title: "Ascot to Heathrow Taxi - Fixed-Price Airport Transfers | CheapCabGrab",
    description:
      "Book a fixed-price taxi from Ascot Racecourse or Ascot station to Heathrow. Licensed drivers, flight tracking, race day pickup planning and no surge pricing.",
    keywords:
      "Ascot to Heathrow taxi, Ascot Racecourse to Heathrow taxi, Royal Ascot to Heathrow transfer, Ascot station to Heathrow taxi, Ascot airport transfer, CheapCabGrab",
    canonical: "https://www.cheapcabgrab.com/ascot-to-heathrow",
    ogTitle: "Ascot to Heathrow Taxi - Fixed Airport Transfers",
    ogDescription:
      "Reliable Ascot to Heathrow taxis with fixed fares, licensed drivers, race day pickup planning and live flight tracking.",
    serviceName: "CheapCabGrab - Ascot to Heathrow Taxi",
    areaServed: ["Ascot Racecourse", "Ascot Station", "Royal Ascot", "Heathrow Airport", "Sunningdale", "Windsor"],
    offerCatalogName: "Ascot to Heathrow Taxi Routes",
    offerName: "Ascot to Heathrow Airport fixed-price taxi",
    offerDescription: "Fixed-price taxi transfers from Ascot Racecourse, Ascot station and local hotels to Heathrow Airport terminals.",
    reservationName: "Taxi booking from Ascot to Heathrow",
  },
  hero: {
    eyebrow: "Ascot to Heathrow taxi route",
    h1: "Ascot to Heathrow Taxi - Fixed-Price Airport Transfers",
    subtitle: "Ascot Racecourse, Ascot station and hotel pickups for Heathrow terminals 2, 3, 4 and 5.",
    body:
      "Royal Ascot runs Tuesday 16 to Saturday 20 June 2026. Book reliable taxis from Ascot to Heathrow with licensed drivers, flight tracking and secure online booking. Fixed upfront fares with no surge pricing.",
  },
  quote: {
    label: "Get instant quotes",
    note: "Free - Fast - No obligation",
    presetPickup: "",
    presetDropoff: "Heathrow Airport",
    pickupPlaceholder: "Enter Ascot pickup postcode or address",
    dropoffPlaceholder: "Heathrow Airport or terminal",
  },
  flags: [
    "Royal Ascot 16-20 June 2026 race day airport transfers",
    "Fixed fares agreed upfront - no meters",
    "Live flight tracking for Heathrow arrivals",
    "Ascot Racecourse, station and hotel pickups",
  ],
  reviewPageKey: "/ascot-to-heathrow",
  reviewAriaLabel: "Ascot to Heathrow customer reviews",
  statusProfile: "ascot",
  lhrStatusKicker: "Live Ascot + Heathrow intelligence",
  lhrStatusTitle: "Live Ascot checks for Ascot to Heathrow taxis",
  floatingCta: "Book Ascot to Heathrow Taxi",
  mobileCta: "Book Ascot to Heathrow Taxi",
  supportNudge: "Need Ascot to Heathrow help?",
  sections: [
    {
      id: "route-overview",
      title: "Ascot to Heathrow at a glance",
      body: "A premium airport transfer route for Ascot Racecourse, Royal Ascot, Ascot station, local hotels and nearby Berkshire pickups.",
      render: () => cardGrid("route-overview-grid", [
        {
          title: "Journey basics",
          items: [
            "Distance: Approx. 15-22 miles depending on pickup point and terminal",
            "Typical time: 30-50 minutes",
            "Typical saloon fare: from GBP55-GBP85",
            "Areas covered: Ascot Racecourse, Ascot station, Sunningdale, Virginia Water and nearby hotels",
          ],
        },
        {
          title: "Great for",
          items: [
            "Royal Ascot racegoers heading to Heathrow",
            "Ascot station to Heathrow transfers",
            "Hotel guests with luggage, hats or picnic items",
            "MPVs and minibuses for race day groups",
          ],
        },
        {
          title: "Local planning links",
          links: [
            { to: "/ascot-taxi", label: "Ascot taxi" },
            { to: "/windsor-to-heathrow", label: "Windsor to Heathrow", className: "route-link-btn route-link-btn-secondary" },
          ],
        },
      ]),
    },
    {
      id: "route-tools",
      title: "Useful tools for your Ascot to Heathrow journey",
      body: "Check Heathrow Airport terminals, live status and race day transport options before you travel.",
      render: () => cardGrid("route-links-grid", [
        { title: "Heathrow Terminal Finder", body: "Check the correct Heathrow terminal before booking or travelling.", link: { to: "/terminalFinder", label: "Open Terminal Finder", className: "route-link-btn" } },
        { title: "Royal Ascot taxi hub", body: "For racecourse, Ascot station, hotel and event guidance, use the Ascot taxi hub.", link: { to: "/ascot-taxi", label: "Open Ascot taxi", className: "route-link-btn" } },
        { title: "Group travel", body: "Need more luggage space from Ascot? Use minibus or coach options for race day groups.", links: [{ to: "/minibus", label: "Minibus" }, { to: "/coach", label: "Coach", className: "route-link-btn route-link-btn-secondary" }] },
      ]),
    },
    {
      id: "route-areas",
      title: "Ascot to Heathrow pickup areas",
      body: "Book from Ascot Racecourse, Ascot station, local hotels, Sunningdale, Virginia Water and nearby Berkshire addresses to Heathrow Airport.",
      render: () => cardGrid("route-areas-grid", [
        {
          title: "Ascot and racecourse pickups",
          items: [
            "Ascot Racecourse to Heathrow Airport",
            "Ascot station to Heathrow Airport",
            "Royal Ascot hotel and hospitality pickups",
            "Sunningdale, Virginia Water and nearby homes",
          ],
          link: { to: "/ascot-taxi", label: "Ascot taxi hub ->" },
        },
        {
          title: "Nearby Heathrow routes",
          items: [
            "Windsor to Heathrow for SL4 hotel stays",
            "Slough to Heathrow for station and hotel pickups",
            "Taplow to Heathrow for Thames-side hotels",
          ],
          links: [
            { to: "/windsor-to-heathrow", label: "Windsor route" },
            { to: "/slough-to-heathrow", label: "Slough route", className: "route-link-btn route-link-btn-secondary" },
            { to: "/taplow-to-heathrow", label: "Taplow route", className: "route-link-btn route-link-btn-secondary" },
          ],
        },
      ]),
    },
    {
      id: "route-faq",
      title: "Ascot to Heathrow taxi - FAQs",
      body: "Route FAQ for Ascot Racecourse and local Heathrow Airport transfers.",
      render: () => (
        <div className="route-faq-grid">
          {[
            ["How long does Ascot to Heathrow take?", "Most Ascot to Heathrow taxi journeys take around 30-50 minutes, depending on race day traffic, pickup point and terminal."],
            ["Can you pick up from Ascot Racecourse?", "Yes. Ascot Racecourse is a regular pickup point, but race day pickup locations can depend on traffic controls and crowd movement."],
            ["Can you collect from Ascot station?", "Yes. Ascot station pickups are useful for passengers connecting from rail to Heathrow or local hotels."],
            ["Do you track Heathrow flights?", "Yes. For Heathrow pickups we monitor your flight and adjust dispatch timing where possible."],
          ].map(([title, body]) => <article className="route-faq-card" key={title}><h3>{title}</h3><p>{body}</p></article>)}
        </div>
      ),
    },
    {
      id: "route-support",
      title: "Need help with an Ascot to Heathrow booking?",
      body: "For race day timing, extra luggage, MPVs, child seats or multi-stop Heathrow Airport journeys, contact support before booking.",
      render: () => (
        <div className="route-support-box">
          <p>Email: <a href="mailto:support@cheapcabgrab.com">support@cheapcabgrab.com</a></p>
          <p>
            Related pages: <Link to="/ascot-taxi">Ascot taxi</Link>,{" "}
            <Link to="/windsor-to-heathrow">Windsor to Heathrow</Link>,{" "}
            <Link to="/slough-to-heathrow">Slough to Heathrow</Link> and{" "}
            <Link to="/taplow-to-heathrow">Taplow to Heathrow</Link>.
          </p>
        </div>
      ),
    },
  ],
};

const AscotToHeathrow = () => (
  <CcgRoutePremiumPage route={ascotToHeathrowRoute} QuoteComponent={PostcodeQuotenew} />
);

export default AscotToHeathrow;
